// Reviews search behavior.
//
// On desktop, the search bar shows recent searches and applies on Enter/arrow.
// On mobile web and the native apps, focusing the search bar opens a full-screen
// takeover pinned to the top of the page (AllTrails-style) with a list of recent
// searches persisted in localStorage. The applied `query` only changes on submit,
// so the reviews list does not refetch on every keystroke and Cancel is a true no-op.

import { searchTakeoverScrollLock } from "./search_takeover_scroll_lock";

const RECENT_SEARCHES_KEY = "skinsort-recent-review-searches";
const MAX_RECENT_SEARCHES = 6;
const MOBILE_BREAKPOINT = 1024; // matches Tailwind's `lg`

function normalizeRecentSearches(searches) {
  const normalized = [];

  for (const term of searches) {
    if (typeof term !== "string") continue;

    const trimmedTerm = term.trim();
    if (!trimmedTerm) continue;
    if (normalized.some((existing) => existing.toLowerCase() === trimmedTerm.toLowerCase())) {
      continue;
    }

    normalized.push(trimmedTerm);
    if (normalized.length === MAX_RECENT_SEARCHES) break;
  }

  return normalized;
}

function loadRecentSearches() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY));
    const searches = normalizeRecentSearches(Array.isArray(stored) ? stored : []);
    persistRecentSearches(searches);
    return searches;
  } catch {
    return [];
  }
}

function persistRecentSearches(searches) {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
  } catch {
    // Ignore storage failures (private browsing, quota, etc.)
  }
}

document.addEventListener("alpine:init", () => {
  window.Alpine.data("reviewSearch", ({ q = "", sort = "popular" } = {}) => ({
    ...searchTakeoverScrollLock(),
    query: q,
    selectedOption: sort,
    searchFocused: false,
    desktopSearchOpen: false,
    draftQuery: q,
    recentSearches: loadRecentSearches(),

    // The filter drawer's frame is lazy, so opening the drawer is what fetches it. Point it at the
    // live search and sort first, since the drawer's "Show N reviews" count is rendered server-side.
    // Re-pointing it at the URL it already holds would refetch and wipe any filters the visitor
    // ticked but hasn't applied yet, so only write a URL that actually changed.
    pointFiltersAt(url) {
      const frame = this.$refs.reviewFilters;
      const expand = (value) => new URL(value, document.baseURI).href;

      if (frame.src && expand(frame.src) === expand(url)) return;

      frame.src = url;
    },

    // Focusing the bar opens the desktop recent-searches dropdown on large
    // viewports, or the mobile takeover on small ones. On mobile the tapped
    // input IS the takeover's input: the bar re-pins itself to the top of the
    // viewport and keeps focus, which is the only way iOS reliably shows and
    // keeps the keyboard (programmatic focus handoffs don't).
    onSearchFocus() {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        this.desktopSearchOpen = true;
      } else {
        this.openSearch();
      }
    },

    // Enter applies the draft via whichever flow matches the viewport.
    submitQuery() {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        this.commitDesktopSearch();
      } else {
        this.submitSearch();
      }
    },

    closeDesktopSearch() {
      this.desktopSearchOpen = false;
    },

    // Desktop is submit-based: Enter or the arrow applies the draft as the search.
    commitDesktopSearch() {
      const term = this.draftQuery.trim();
      this.query = term;
      if (term) this.addRecentSearch(term);
      this.desktopSearchOpen = false;
      this.scrollToSearchBar();
    },

    applyRecentSearch(term) {
      this.draftQuery = term;
      this.query = term;
      this.addRecentSearch(term);
      this.desktopSearchOpen = false;
      this.scrollToSearchBar();
    },

    // The user's tap already focused the real input, so opening the takeover
    // is pure styling: the bar re-pins fixed to the viewport top (via its
    // :class binding), the overlay shows underneath it, and the page locks.
    // Focus never moves, so the keyboard appears and stays without any
    // programmatic focus tricks.
    openSearch() {
      if (this.searchFocused) return;
      this.draftQuery = this.query;
      this.searchFocused = true;
      this.$nextTick(() => this.lockBodyScroll());
    },

    closeSearch() {
      this.searchFocused = false;
      // Discard any unsubmitted text so the bar reflects the applied search.
      this.draftQuery = this.query;
      this.$refs.searchInput?.blur();
      this.unlockBodyScroll();
    },

    clearDraft() {
      this.draftQuery = "";
      this.$refs.searchInput?.focus();
    },

    submitSearch() {
      const term = this.draftQuery.trim();
      this.query = term;
      if (term) this.addRecentSearch(term);
      this.closeSearch();
      this.scrollToSearchBar();
    },

    // Bring the reviews back to the top so the freshly filtered results read
    // like a results page instead of leaving the user at their previous scroll
    // spot. We can't scrollIntoView the search bar itself: it's position:sticky
    // and already stuck to the top when scrolled, so the browser treats it as
    // in-view and no-ops. Instead scroll to the non-sticky heading above it,
    // offset by the masthead height the bar pins under (lg:top-24 = 96px).
    scrollToSearchBar() {
      this.$nextTick(() => {
        const anchor = this.$refs.reviewsTop;
        if (!anchor) return;
        const offset = window.innerWidth >= MOBILE_BREAKPOINT ? 96 : 0;
        const top = anchor.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      });
    },

    selectRecentSearch(term) {
      this.draftQuery = term;
      this.submitSearch();
    },

    addRecentSearch(term) {
      this.recentSearches = normalizeRecentSearches([term, ...this.recentSearches]);
      persistRecentSearches(this.recentSearches);
    },

    clearRecentSearches() {
      this.recentSearches = [];
      persistRecentSearches(this.recentSearches);
    },
  }));
});
